import { identity, sortBy } from 'lodash';
import styled from 'styled-components';
import { Table } from 'reactstrap';
import CheckIcon from '../icons/CheckIcon';
import {
  asPercentage,
  asQuantity,
  asQuantityWithUnit,
} from '../../utils/formatters';
import { useDataScope } from '../../utils/hooks';
import { captureEvent } from '../../utils/analytics';
import UnderlinedLink from '../basics/UnderlinedLink';

const CROP_BASE_COLS = (isTemplate, cropPath, scope) => [
  {
    accessor: 'crop',
    displayString: isTemplate ? 'Crop Template' : 'Crop',
    // eslint-disable-next-line react/display-name
    format: (crop) => (
      <UnderlinedLink
        to={`../../${cropPath}/${crop.id}/edit`}
        onClick={() =>
          captureEvent('capex-reference-link-clicked', {
            scope,
            linkTo: 'crop',
          })
        }
      >
        {crop.name}
      </UnderlinedLink>
    ),
  },
];

const CROP_USAGE_COL = {
  accessor: 'referencesCapex',
  displayString: 'CapEx Usage',
  // eslint-disable-next-line react/display-name
  format: (valid) => (valid ? <CheckIcon /> : null),
  style: { verticalAlign: 'middle', textAlign: 'center' },
};

const CROP_OP_COL = (cropPath, scope) => ({
  accessor: 'referencingOperations',
  displayString: 'Operations',
  // eslint-disable-next-line react/display-name
  format: (ops, { crop }) =>
    !ops || ops.length === 0 ? (
      'None'
    ) : (
      <>
        {sortBy(ops, (op) => op.name).map((op, i) => (
          <span key={op.id}>
            <UnderlinedLink
              to={`../../${cropPath}/${crop.id}/operations/${op.id}`}
              onClick={() =>
                captureEvent('capex-reference-link-clicked', {
                  scope,
                  linkTo: 'operation',
                })
              }
            >
              {op.name}
            </UnderlinedLink>
            {i < ops.length - 1 && <span>, </span>}
          </span>
        ))}
      </>
    ),
});

const AREA_USAGE_COLS = [
  {
    accessor: 'acreage',
    displayString: 'Acres for Proration',
    format: (value) =>
      asQuantity(value, { digits: 1, unit: 'acres', singular: 'acre' }),
  },
  {
    accessor: 'proportionOfAcreage',
    displayString: 'Share of CapEx Cost',
    format: (value) => asPercentage(value * 100, { digits: 1 }),
  },
];

const TIME_USAGE_COLS = [
  {
    accessor: 'totalCapexHours',
    displayString: 'Time for Proration',
    format: asQuantityWithUnit('hours', 'hour'),
  },
  {
    accessor: 'proportionOfTime',
    displayString: 'Share of CapEx Cost',
    format: (value) => asPercentage(value * 100, { digits: 1 }),
  },
];

const OutlinedRow = styled.tr`
  border: 1px solid var(--table-border-color);
`;

const CapexReferenceCropTable = styled(({ resource, isTemplate, ...props }) => {
  const scope = useDataScope();
  const cropPath = isTemplate ? 'templates' : 'crops';
  let cols = CROP_BASE_COLS(isTemplate, cropPath, scope);
  if (!isTemplate) {
    cols = [...cols, CROP_USAGE_COL];
    if (resource?.allCrops?.[0]?.proratedByTime) {
      cols = [...cols, ...TIME_USAGE_COLS];
    } else {
      cols = [...cols, ...AREA_USAGE_COLS];
    }
  }
  // the operations column should come last
  cols = [...cols, CROP_OP_COL(cropPath, scope)];

  return (
    <Table {...props}>
      <thead>
        <tr>
          {cols.map(({ displayString }) => (
            <th key={displayString}>{displayString}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {resource?.allCrops.map((item) => (
          <OutlinedRow key={item.crop.id}>
            {cols.map(({ accessor, format = identity, style }) => {
              return (
                <td style={style} key={accessor}>
                  {format(item[accessor], item)}
                </td>
              );
            })}
          </OutlinedRow>
        ))}
      </tbody>
    </Table>
  );
})`
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  border-spacing: 0;
  border-collapse: collapse;
  white-space: nowrap;
  th {
    border: none !important;
  }
  tr:nth-child(even) {
    background-color: rgba(33, 33, 33, 0.05);
  }
`;

export default CapexReferenceCropTable;
